import type { MatchEvent, NetworkPlayer } from '../network/protocol';
import { weaponDefinitions } from '../game/content/weapons';

export type MatchFeedTone = 'neutral' | 'success' | 'danger' | 'warning';

export interface MatchFeedMessage {
  text: string;
  tone: MatchFeedTone;
}

const maximumMessages = 4;
const messageLifetimeMilliseconds = 5_500;

export function describeMatchEvent(
  event: MatchEvent,
  players: readonly NetworkPlayer[],
  localPlayerID: string,
): MatchFeedMessage | null {
  const name = (id?: string): string => {
    if (!id) return 'THE TRAIN';
    if (id === localPlayerID) return 'YOU';
    return players.find((player) => player.id === id)?.displayName.toUpperCase() ?? 'A SCRAPPER';
  };
  const possessiveName = (id?: string): string => id === localPlayerID ? 'YOUR' : `${name(id)}'S`;
  if (event.type === 'player_knocked_out' || event.type === 'player_knocked_off') {
    const source = name(event.sourceId);
    const target = name(event.targetId);
    return {
      text: `${source} KNOCKED ${target} OFF THE LINE`,
      tone: event.targetId === localPlayerID ? 'danger' : event.sourceId === localPlayerID ? 'success' : 'neutral',
    };
  }
  if (event.type === 'takedown_bonus' && event.sourceId === localPlayerID) {
    const finalSignal = event.outcome === 'final' ? ' // FINAL PAID BOUNTY THIS TRAIN' : '';
    return { text: `TAKEDOWN BOUNTY SECURED // +₵${event.value ?? 0}${finalSignal}`, tone: 'success' };
  }
  if (event.type === 'unarmed_action' && event.label === 'grab_blocked') {
    if (event.sourceId === localPlayerID) {
      return { text: `${name(event.targetId)} BRACED YOUR GRAB // FLANK THEM`, tone: 'warning' };
    }
    if (event.targetId === localPlayerID) {
      return { text: `YOU BRACED ${name(event.sourceId)}'S GRAB`, tone: 'success' };
    }
    return null;
  }
  if (event.type === 'player_grabbed') {
    return {
      text: `${name(event.sourceId)} GRABBED ${name(event.targetId)}`,
      tone: event.targetId === localPlayerID ? 'danger' : event.sourceId === localPlayerID ? 'warning' : 'neutral',
    };
  }
  if (event.type === 'grab_broken') {
    return {
      text: `${name(event.sourceId)} BROKE ${possessiveName(event.targetId)} GRIP`,
      tone: event.sourceId === localPlayerID ? 'success' : event.targetId === localPlayerID ? 'danger' : 'neutral',
    };
  }
  if (event.type === 'bank_interrupted') {
    if (event.targetId === localPlayerID) {
      return { text: `${name(event.sourceId)} BROKE YOUR BANK LINK`, tone: 'danger' };
    }
    if (event.sourceId === localPlayerID) {
      return { text: `YOU DENIED ${name(event.targetId)}'S CASHOUT`, tone: 'success' };
    }
    return { text: `${name(event.sourceId)} DENIED ${name(event.targetId)}'S CASHOUT`, tone: 'warning' };
  }
  if (event.type === 'player_thrown') {
    return {
      text: `${name(event.sourceId)} THREW ${name(event.targetId)}`,
      tone: event.targetId === localPlayerID ? 'danger' : event.sourceId === localPlayerID ? 'success' : 'neutral',
    };
  }
  if (event.type === 'player_extracted') {
    return {
      text: `${name(event.playerId)} BANKED ₵${event.value ?? 0}`,
      tone: event.playerId === localPlayerID ? 'success' : 'neutral',
    };
  }
  if (event.type === 'train_advanced') {
    return {
      text: `${name(event.playerId)} RISKED TRAIN ${event.value ?? 2} // ${event.label ?? 'MULTIPLIER HELD'}`,
      tone: event.playerId === localPlayerID ? 'success' : 'warning',
    };
  }
  if (event.type === 'train_arrived') {
    return {
      text: `TRAIN ${event.value ?? 2} COUPLED // LOCOMOTIVE TRANSFER OPEN`,
      tone: 'success',
    };
  }
  if (event.type === 'coupler_armed') {
    return { text: `${name(event.playerId ?? event.sourceId)} ARMED THE REAR COUPLER`, tone: 'warning' };
  }
  if (event.type === 'coupler_separated') {
    return { text: 'REAR SECTION DETACHED // GAP OPENING', tone: 'danger' };
  }
  if (event.type === 'rear_cargo_staged') {
    const count = Math.max(1, event.value ?? 1);
    return {
      text: `REAR SALVAGE CAUGHT // FORWARD FLATBED // ${count} ${count === 1 ? 'SIGNAL' : 'SIGNALS'}`,
      tone: 'warning',
    };
  }
  if (event.type === 'supply_spawned' && event.label && event.label in weaponDefinitions) {
    const weapon = weaponDefinitions[event.label as keyof typeof weaponDefinitions];
    const section = supplyRouteSection(event.position?.z);
    return { text: `FREIGHT RESUPPLY // ${weapon.label} ${section} // Q/E/R LINEWIDE`, tone: 'success' };
  }
  if (event.type === 'loot_dropped' && (event.value ?? 0) >= 50) {
    return {
      text: `${name(event.playerId)} DROPPED ₵${event.value ?? 0} ON THE TRAIN`,
      tone: event.playerId === localPlayerID ? 'danger' : 'warning',
    };
  }
  return null;
}

export function supplyRouteSection(z?: number): string {
  if (typeof z !== 'number') return 'ON LINE';
  if (z >= 126) return 'REAR LINE';
  if (z >= 63) return 'MIDLINE';
  return 'FRONT LINE';
}

export class MatchFeed {
  private readonly element: HTMLOListElement;
  private readonly timers = new Set<number>();

  public constructor(root: ParentNode) {
    const element = root.querySelector<HTMLOListElement>('[data-match-feed]');
    if (!element) throw new Error('Missing match activity feed');
    this.element = element;
  }

  public push(event: MatchEvent, players: readonly NetworkPlayer[], localPlayerID: string): void {
    const message = describeMatchEvent(event, players, localPlayerID);
    if (!message) return;
    const item = document.createElement('li');
    item.dataset.tone = message.tone;
    item.textContent = message.text;
    this.element.prepend(item);
    while (this.element.children.length > maximumMessages) this.element.lastElementChild?.remove();
    const timer = window.setTimeout(() => {
      item.classList.add('is-leaving');
      const removalTimer = window.setTimeout(() => {
        item.remove();
        this.timers.delete(removalTimer);
      }, 240);
      this.timers.add(removalTimer);
      this.timers.delete(timer);
    }, messageLifetimeMilliseconds);
    this.timers.add(timer);
  }

  public clear(): void {
    for (const timer of this.timers) window.clearTimeout(timer);
    this.timers.clear();
    this.element.replaceChildren();
  }
}
